import {useEffect, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {COMPACT_ROOMS_AMOUNT} from 'components/Accommodation/Rooms/constants'
import {scrollToSection} from 'components/ScrollSpy'
import {getScrollTargetSpecificRoom} from 'hooks/useHandleScrollToClickedOfferTarget/utils'
import {useRoomsSectionScrollTarget} from 'hooks/useRoomsSectionScrollTarget'
import {
  getHasActiveRoomFilter,
  getHasOffersSearchErrored,
  getHasRoomsSearchErrored,
  getIsVioOfferOrBundleSelected,
  getMetaBrandHasRooms
} from 'modules/accommodation/selectors'
import {expandRoomsView} from 'modules/accommodation/slice'
import {getIsNoDatesSearch} from 'modules/search/selectors'
import {
  getFilteredRoomsData,
  getIsRoomsPollingComplete,
  getIsRoomsQueryLoading,
  getIsRoomsResultEmpty,
  getRoomCardEntities,
  getUnfilteredRooms
} from 'modules/searchApi/selectors'
import {stripUrlParamsFromHistory} from 'utils/url'

import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page,
  Team
} from '@vio/core/src/analytics/types/Events'
import {DF_FLOW_PARAM} from '@vio/shared/src/dealFreeze/utils/url/constants'

const SCROLL_TO_ROOMS_PARAM = 'scrollToRooms'
const SCROLL_TO_ROOM_PARAM = 'scrollToRoom'

// ~10s of frames: on desktop the section anchor mounts only after the hotel
// content query resolves, well past the default waitForElement budget
export const EARLY_SECTION_SCROLL_MAX_FRAMES = 600

export const ROOM_DEEP_LINK_OUTCOMES = {
  matched: 'matched',
  filteredOut: 'filteredOut',
  notFound: 'notFound',
  roomsErrored: 'roomsErrored',
  offersErrored: 'offersErrored',
  sectionScrolled: 'sectionScrolled'
} as const

type RoomDeepLinkOutcome =
  (typeof ROOM_DEEP_LINK_OUTCOMES)[keyof typeof ROOM_DEEP_LINK_OUTCOMES]

interface Options {
  hotelId: string
}

/**
 * Consumes the `scrollToRoom=<masterId>` / `scrollToRooms=1` deep-link params.
 * Scrolls to the rooms section as soon as it renders, then refines once offers
 * and rooms polling settle: to the matching room card (expanding the list when
 * it sits beyond the compact slice), or back to the rooms section when there
 * is no match. Strips the params after consumption so navigation and remounts
 * cannot re-trigger it, and reports the outcome so stale-masterId links are
 * measurable.
 */
export const useScrollToRoomFromUrlParams = ({hotelId}: Options): void => {
  const dispatch = useDispatch()
  const isRoomsLoading = useSelector(getIsRoomsQueryLoading)
  const isRoomsPollingComplete = useSelector(getIsRoomsPollingComplete)
  const {isComplete} = useAccommodationTopOffers({hotelId})
  const filteredRoomsData = useSelector(getFilteredRoomsData)
  const roomCardEntities = useSelector(getRoomCardEntities)
  const unfilteredRooms = useSelector(getUnfilteredRooms)
  const isRoomsResultEmpty = useSelector(getIsRoomsResultEmpty)
  const hasRoomsErrored = useSelector(state =>
    getHasRoomsSearchErrored(state, hotelId)
  )
  const hasOffersErrored = useSelector(state =>
    getHasOffersSearchErrored(state, hotelId)
  )
  const isVioOfferOrBundleSelected = useSelector(getIsVioOfferOrBundleSelected)
  const brandHasRooms = useSelector(getMetaBrandHasRooms)
  const hasActiveRoomFilters = useSelector(getHasActiveRoomFilter)
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)
  const roomsSectionScrollTarget = useRoomsSectionScrollTarget()
  const hasConsumedRef = useRef(false)
  const hasEarlyScrolledRef = useRef(false)
  const cancelScrollRef = useRef<() => void>()

  // Unmount-scoped so a dependency change cannot cancel an in-flight scroll
  useEffect(
    () => () => {
      cancelScrollRef.current?.()
      hasEarlyScrolledRef.current = false
    },
    []
  )

  useEffect(() => {
    if (hasConsumedRef.current) return

    const params = new URLSearchParams(window.location.search)
    const masterId = params.get(SCROLL_TO_ROOM_PARAM)
    const wantsSectionScroll = params.get(SCROLL_TO_ROOMS_PARAM) === '1'
    // Presence, not value: malformed values (scrollToRooms=0, empty
    // scrollToRoom) must still be consumed and stripped, or they linger in the
    // URL and leak into every later in-app navigation
    if (
      !params.has(SCROLL_TO_ROOM_PARAM) &&
      !params.has(SCROLL_TO_ROOMS_PARAM)
    ) {
      return
    }

    const stripParams = () =>
      stripUrlParamsFromHistory([SCROLL_TO_ROOM_PARAM, SCROLL_TO_ROOMS_PARAM])

    const rooms = filteredRoomsData?.rooms ?? []

    const startScroll = (
      target: string,
      options?: Parameters<typeof scrollToSection>[1]
    ) => {
      cancelScrollRef.current?.()
      cancelScrollRef.current = scrollToSection(target, options)
    }

    const track = (
      outcome: RoomDeepLinkOutcome,
      extra: Record<string, unknown> = {}
    ) => {
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.RoomDeepLink,
          action: Action.Succeeded,
          page: Page.Accommodation,
          // Engagement's source, not the AP's Select context: this measures the
          // PriceWatch email funnel
          team: Team.Retention,
          payload: {
            outcome,
            requestedRoomMasterId: masterId,
            matchedRoomId: null,
            roomPosition: null,
            roomCount: rooms.length,
            roomsResultEmpty: isRoomsResultEmpty,
            hasActiveRoomFilters,
            ...extra
          }
        })
      )
    }

    // Competing scroll owners (DF resume, clicked-offer flow) or a brand whose
    // rooms never render: consume silently.
    const isDfFlow = params.has(DF_FLOW_PARAM)
    if (isDfFlow || isVioOfferOrBundleSelected || !brandHasRooms) {
      hasConsumedRef.current = true
      stripParams()
      return
    }

    // Must precede the completeness gates: neither ever opens once its search
    // errored, and a rooms search never even starts on an offers error.
    if (hasRoomsErrored || hasOffersErrored) {
      hasConsumedRef.current = true
      // The user asked to see rooms, so land them at the section's real state
      // (sold out, empty) rather than leaving them at the hero
      if (masterId || wantsSectionScroll) {
        startScroll(roomsSectionScrollTarget, {
          waitForElement: true,
          maxWaitFrames: EARLY_SECTION_SCROLL_MAX_FRAMES
        })
      }
      track(
        hasRoomsErrored
          ? ROOM_DEEP_LINK_OUTCOMES.roomsErrored
          : ROOM_DEEP_LINK_OUTCOMES.offersErrored
      )
      stripParams()
      return
    }

    // Rooms re-sort on every polling chunk, so the room-level resolution below
    // acts only on the settled list (this also holds no-dates landings until
    // the user picks dates). Don't make the user wait for that: scroll to the
    // section as soon as it renders. Skipped on no-dates landings, where a
    // late-appearing section would yank the user out of the date picker.
    const isSettled = isComplete && isRoomsPollingComplete && !isRoomsLoading
    if (
      !isSettled &&
      !hasEarlyScrolledRef.current &&
      !isNoDatesSearch &&
      (masterId || wantsSectionScroll)
    ) {
      hasEarlyScrolledRef.current = true
      startScroll(roomsSectionScrollTarget, {
        waitForElement: true,
        maxWaitFrames: EARLY_SECTION_SCROLL_MAX_FRAMES
      })
    }
    if (!isSettled) return

    hasConsumedRef.current = true

    if (masterId) {
      const matchedRoom = rooms.find(room => room.masterId === masterId)
      if (matchedRoom) {
        // The compact slice is cut from the re-sorted display list (split
        // booking included), not from the raw filtered order
        const displayIndex = roomCardEntities.findIndex(
          entity => entity.id === matchedRoom.id
        )
        if (displayIndex >= COMPACT_ROOMS_AMOUNT) {
          dispatch(expandRoomsView())
        }
        startScroll(getScrollTargetSpecificRoom(matchedRoom.id), {
          waitForElement: true
        })
        track(ROOM_DEEP_LINK_OUTCOMES.matched, {
          matchedRoomId: matchedRoom.id,
          roomPosition: displayIndex + 1
        })
      } else {
        const outcome = unfilteredRooms.some(room => room.masterId === masterId)
          ? ROOM_DEEP_LINK_OUTCOMES.filteredOut
          : ROOM_DEEP_LINK_OUTCOMES.notFound
        startScroll(roomsSectionScrollTarget, {waitForElement: true})
        track(outcome)
      }
    } else if (wantsSectionScroll) {
      startScroll(roomsSectionScrollTarget, {waitForElement: true})
      track(ROOM_DEEP_LINK_OUTCOMES.sectionScrolled)
    }

    stripParams()
  }, [
    isComplete,
    isRoomsPollingComplete,
    isRoomsLoading,
    hasRoomsErrored,
    hasOffersErrored,
    isVioOfferOrBundleSelected,
    brandHasRooms,
    isNoDatesSearch,
    filteredRoomsData,
    roomCardEntities,
    unfilteredRooms,
    isRoomsResultEmpty,
    roomsSectionScrollTarget,
    hasActiveRoomFilters,
    dispatch
  ])
}
